import React, { useState } from 'react';
import { isMobile } from 'react-device-detect';
import ReactDOM from 'react-dom';
import Carousel from 'components/Carousel';
import NiceModal from 'components/modals/NiceModal';
import CarouselPictureModal from 'modals/CarouselPictureModal/CarouselPictureModal';
import styled, { keyframes } from 'styled-components';
import { getMediaRatio, isVerticalMedia } from 'utils/mediaUtils';
import { isBlank } from 'utils/storeUtils';

interface MediaItem {
  url: string;
  videoCoverUrl?: string;
  isVideo?: boolean;
  order?: number;
}

interface MediaPackGridModalProps {
  title: string;
  mediaItems: MediaItem[];
  onClose: () => void;
}

const CloseButton = styled.div`
  @media (max-width: 430px) {
    position: fixed;
    right: 24px;
    top: 24px;
    z-index: 1000;
    cursor: pointer;

    img {
      width: 20px;
      height: 20px;
    }
  }
`;

const MediaPackGridModal: React.FC<MediaPackGridModalProps> = (props) => {
  const { title, mediaItems, onClose } = props;
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | undefined>(undefined);
  const [loadedImages, setLoadedImages] = useState<Record<string, boolean>>({});
  const [mediaRatios, setMediaRatios] = useState<Record<string, number>>({});
  const [isCurrentImageVertical, setIsCurrentImageVertical] = useState(false);

  const sortedMediaItems = [...mediaItems].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));

  const formattedMediaItems = sortedMediaItems.map((item) => ({
    ...item,
    isVideo: !!item?.videoCoverUrl,
  }));

  const handleMediaLoad = (url: string, e: React.SyntheticEvent<HTMLImageElement | HTMLVideoElement>) => {
    const element = e.target as HTMLImageElement | HTMLVideoElement;
    setLoadedImages((prev) => ({ ...prev, [url]: true }));
    
    const ratio = getMediaRatio(element);
    setMediaRatios((prev) => ({ 
      ...prev, 
      [url]: ratio 
    }));
  };

  const handleImageError = (url: string) => {
    setLoadedImages((prev) => ({ ...prev, [url]: false }));
  };

  const transformMediaItems = (items: MediaItem[]) => {
    return items.map((item, index) => ({
      id: String(index),
      url: item.isVideo ? item.videoCoverUrl || item.url : item.url,
      type: item.isVideo ? ('video' as const) : ('image' as const),
    }));
  };

  return ReactDOM.createPortal(
    <NiceModal
      onClose={onClose}
      closeOnOutsideClick={false}
      customCloseButton={isMobile && isBlank(selectedPhotoIndex) ? <CloseButton /> : undefined}
    >
      <ModalContent>
        <TopBar>
          <Title>{title}</Title>
        </TopBar>

        <GridContainer>
          {formattedMediaItems.map((item, index) => {
            const imageUrl = item.isVideo ? (item.videoCoverUrl || item.url) : item.url;
            const ratio = mediaRatios[imageUrl] || 1;
            const isVertical = isVerticalMedia(ratio);       
            return (
              <GridItem 
                key={index} 
                onClick={() => setSelectedPhotoIndex(index)}
                isVertical={isVertical}
              >
                {item.isVideo && (
                  <VideoWrapper>
                    <video
                      src={item.videoCoverUrl}
                      poster={item.url}
                      controls={false}
                      muted
                      loop
                      playsInline
                      onLoadedMetadata={(e) => handleMediaLoad(item.videoCoverUrl || item.url, e)}
                    />
                    <VideoIndicator />
                  </VideoWrapper>
                )}
                {!item.isVideo && (
                  <>
                    {!loadedImages[item.url] && <SkeletonItem />}
                    <Image
                      src={item.url}
                      alt={`Media item ${index + 1}`}
                      style={{ display: loadedImages[item.url] ? 'block' : 'none' }}
                      onLoad={(e) => handleMediaLoad(item.url, e)}
                      onError={() => handleImageError(item.url)}
                    />
                  </>
                )}
              </GridItem>
            );
          })}
        </GridContainer>

        {!isBlank(selectedPhotoIndex) && (
          <CarouselPictureModal
            onClose={() => setSelectedPhotoIndex(undefined)}
            closeOnOutsideClick={false}
            isVertical={isCurrentImageVertical}
          >
            <Carousel
              mediaItems={transformMediaItems(formattedMediaItems)}
              initialIndex={selectedPhotoIndex}
              onVerticalChange={setIsCurrentImageVertical}
            />
          </CarouselPictureModal>
        )}
      </ModalContent>
    </NiceModal>,
    document.body
  );
};

export default MediaPackGridModal;

const ModalContent = styled.div`
  height: 686px;
  min-height: 686px;
  width: 945px;
  min-width: 945px;
  max-width: 945px;
  background: #1A1A1A;
  border-radius: 12px;
  padding: 20px;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;

  &::-webkit-scrollbar {
    width: 6px; 
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #333;
    border-radius: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }

  scrollbar-width: thin;
  scrollbar-color: #333 transparent;

  @media (max-width: 720px) {
    width: 100vw;
    min-width: 100vw;
    max-width: 100vw;
    height: calc(var(--vh, 1vh) * 100);
    min-height: calc(var(--vh, 1vh) * 100);
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;

    padding: 0;
    margin: 0;
    border-radius: 0;
  }
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-shrink: 0;

  @media (max-width: 720px) {
    margin-bottom: 32px;
    padding: 18px 18px 0;
  }
`;

const Title = styled.h2`
  font-family: 'Nunito Sans';
  font-size: 24px;
  font-weight: 700;
  color: #FFFFFF;
  margin: 0;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 287.00px);
  grid-auto-rows: 287px;
  gap: 16px;
  width: 100%;
  flex-grow: 1;
  overflow-y: auto;
  justify-content: center;
  overflow-x: hidden;

  @media (max-width: 720px) {
    overflow-x: visible;
    display: grid;
    grid-template-columns: repeat(2, calc(50% - 6px));
    grid-auto-rows: minmax(200px, auto);
    row-gap: 12px;
    column-gap: 12px;
    width: calc(100% - 36px);
    margin: 0 18px;
    justify-content: space-between;
    overflow-y: visible;
    flex-grow: 1;
    align-content: flex-start;
    padding-bottom: 20px;
  }

  @media (max-width: 430px) {
    grid-template-columns: 100%;
    grid-auto-rows: auto;
    row-gap: 24px;
    justify-content: center;
  }
`;

const GridItem = styled.div<{ isVertical?: boolean }>`
  width: 290.58px;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  background: #131313;
  position: relative;
  grid-row: ${props => props.isVertical ? 'span 2' : 'span 1'};

  &:hover {
    opacity: 0.9;
  }

  @media (max-width: 720px) {
    width: 100%;
    margin-bottom: 4px;
    ${props => props.isVertical && `
      aspect-ratio: 0.5;
    `}
    ${props => !props.isVertical && `
      aspect-ratio: 1;
    `}
  }

  @media (max-width: 430px) {
    max-width: 100%;
    width: 100%;
    margin-bottom: 8px;
    ${props => props.isVertical && `
      aspect-ratio: 0.5;
      height: auto;
    `}
    ${props => !props.isVertical && `
      aspect-ratio: 1;
      height: auto;
    `}
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;

const VideoWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const VideoIndicator = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  transition: background 0.2s ease;

  &::before {
    content: '';
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 12px 0 12px 20px;
    border-color: transparent transparent transparent white;
    opacity: 0.8;
    transition: opacity 0.2s ease;
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const SkeletonItem = styled.div`
  aspect-ratio: 1;
  border-radius: 12px;
  background: linear-gradient(to right, #1A1A1A 4%, #2A2A2A 25%, #1A1A1A 36%);
  background-size: 1000px 100%;
  animation: ${shimmer} 3.5s infinite linear;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
`;
